import './styles.scss';

import { ReactElement, ReactNode } from 'react';

import { DynamicCellRenderer } from '@/common/Table/cells/DynamicCell';
import ProgressBar from '@/common/ProgressBar';
import { ProgressBarTypes } from '@/common/ProgressBar/types';
import { getClasses } from '@/utils';

export type ProgressBarCellProps = {
  now: number;
  min?: number;
  max: number;
  variant?: ProgressBarTypes.Props['variant'];
  className?: string;
};

const ProgressBarCell = ({ now, min = 0, max, variant, className }: ProgressBarCellProps): ReactElement => (
  <div className={getClasses('ProgressBarCell', className)}>
    <div className="ProgressBarCell-Bar">
      <ProgressBar now={now} min={min} max={max} variant={variant} />
    </div>
    <div className="ProgressBarCell-Label">
      {now}/{max}
    </div>
  </div>
);

export default ProgressBarCell;

/**
 * Value pattern agreed with BE: `PROGRESS::<now>/<max>`, e.g. "PROGRESS::24/30".
 * min is always assumed to be 0
 */
const PROGRESS_BAR_PATTERN = /^PROGRESS::(\d+)\/(\d+)$/;

export type ParsedProgressBarValue = Pick<ProgressBarCellProps, 'now' | 'min' | 'max'>;

export const parseProgressBarValue = (value: string): ParsedProgressBarValue | null => {
  const match = value.match(PROGRESS_BAR_PATTERN);
  if (!match) return null;

  const [, now, max] = match;
  return { now: Number(now), min: 0, max: Number(max) };
};

export const progressBarCellRenderer: DynamicCellRenderer = {
  test: (value: string) => PROGRESS_BAR_PATTERN.test(value),
  render: (value: string): ReactNode => {
    const parsed = parseProgressBarValue(value);
    return parsed ? <ProgressBarCell {...parsed} /> : value;
  },
};
